import { Location } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ComponentStore, tapResponse } from "@ngrx/component-store";
import { BehaviorSubject, Subject } from "rxjs";
import { map, switchMap, takeUntil } from "rxjs/operators";
import { formControlValueChanges } from "src/app/core/v2/utils";
import { SpecialPurposeAssetClass } from "src/app/core/v3/comparable/special-purpose";
import { SpecialPurposeAssetClassAPIService } from "src/app/core/v3/comparable/special-purpose/special-purpose-service";
import { SpecialPurposeComparableFormComponent } from "../form/special-purpose-comparable-form.component";

interface State {
  assetClass?: SpecialPurposeAssetClass
}

@Component({
  selector: 'app-special-purpose-comparable-edit',
  templateUrl: './special-purpose-comparable-edit.component.html',
  styleUrls: [
    '../../complex-btn.scss'
  ],
  providers: [ComponentStore]
})
export class SpecialPurposeComparableEditComponent implements OnInit, OnDestroy {
  @ViewChild(SpecialPurposeComparableFormComponent, {static: true}) 
  formComponent: SpecialPurposeComparableFormComponent

  assetClass$ = this._store.select(state => state.assetClass)

  formSelectionCtrl = new FormControl<number>(2);
  formType$ = formControlValueChanges(this.formSelectionCtrl).pipe(
    map(value => value == 2 ? 'simple' : 'complex')
  )

  title$ = this._store.select(
    this.assetClass$,
    (ac) => {
      if (!ac) return 'Add Special Purpose Type Property'
      return `Edit ${ac.ref_num} Special Purpose Type Property`
    }
  )
  backUrl$ = this._store.select(
    this.assetClass$,
    (ac) => {
      if (!ac) return '../list'
      return '../../list'
    }
  )

  private _onDestroy$ = new Subject<void>()

  constructor(
    private _store: ComponentStore<State>,
    private _activatedRoute: ActivatedRoute,
    private _apiService: SpecialPurposeAssetClassAPIService,
    private _location: Location
  ) {
    this._store.setState({})

    this._activatedRoute.params.pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(params => {
      if (!params.id) return;
      const id = Number(params.id)
      this._fetch(id)
    })
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this._onDestroy$.next()
    this._onDestroy$.complete()
  }

  onSubmit(isComplete: boolean = true) {
    const result = this.formComponent.submit(isComplete);
    if (!result) return;
    const {ac: assetClass, fileList} = result 
    if (assetClass.id) {
      this._update({ac: assetClass, fileList})
    } else {
      this._create({ac: assetClass, fileList})
    }
  }

  private _fetch = this._store.effect<number>(id$ =>
    id$.pipe(
      switchMap(id => 
        this._apiService.fetch(id).pipe(
          tapResponse(
            (response) => {
              this._store.patchState({assetClass: response})
              this.formSelectionCtrl.patchValue(response.formType)
            },
            (err) => {
              console.error(err)
            }
          )
        )
      )
    )
  )

  private _create = this._store.effect<{ac: SpecialPurposeAssetClass, fileList: any[]}>(payload$ =>
    payload$.pipe(
      switchMap(({ac, fileList}) => 
        this._apiService.store(ac, fileList).pipe(
          tapResponse(
            (response) => {
              this._location.back()
            },
            (err) => {
              console.error(err)
            }
          )
        )
      )
    )
  )

  private _update = this._store.effect<{ac: SpecialPurposeAssetClass, fileList: any[]}>(payload$ =>
    payload$.pipe(
      switchMap(({ac, fileList}) => 
        this._apiService.update(ac, fileList).pipe(
          tapResponse(
            (response) => {
              this._location.back()
            },
            (err) => {
              console.error(err)
            }
          )
        )
      )
    )
  )
}